/**
 * CustomAdsPanel - Admin panel for managing Custom Ads
 * 
 * Sections:
 * - Placements: Registry-based placement management with wired status
 * - Creatives: Ad content with drag/drop image upload
 * - Campaigns: Link creatives to placements with scheduling
 * - Reports: MVP analytics (impressions, clicks, CTR)
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { AdminRecordModal, AdminModalFooter } from "../../components/admin/AdminRecordModal";
import { toast } from "sonner";
import {
  Plus,
  Trash,
  PencilSimple,
  Image as ImageIcon,
  Link as LinkIcon,
  Target,
  CheckCircle,
  X,
  ArrowClockwise,
  Upload,
  Warning,
  Plugs,
  PlugsConnected,
} from "phosphor-react";
import { AD_PLACEMENTS, isPlacementWired } from "../../constants/adPlacements";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export function CustomAdsPanel() {
  const [activeSection, setActiveSection] = useState("placements");
  
  // Placements state - merged registry + DB
  const [dbPlacements, setDbPlacements] = useState([]);
  const [loadingPlacements, setLoadingPlacements] = useState(true);
  
  // Creatives state
  const [creatives, setCreatives] = useState([]);
  const [loadingCreatives, setLoadingCreatives] = useState(true);
  const [showCreativeDialog, setShowCreativeDialog] = useState(false);
  const [editingCreative, setEditingCreative] = useState(null);
  const [creativeForm, setCreativeForm] = useState({
    name: "",
    creative_type: "image",
    image_url: "",
    image_key: "",
    headline: "",
    body: "",
    cta_text: "Learn More",
    destination_url: "",
    enabled: true,
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  
  // Campaigns state
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [campaignForm, setCampaignForm] = useState({
    name: "",
    creative_id: "",
    placement_keys: [],
    weight: 1,
    frequency_cap_per_day: 0,
    start_date: "",
    end_date: "",
    enabled: true,
  });
  
  // Reports state
  const [reports, setReports] = useState(null);
  const [loadingReports, setLoadingReports] = useState(true);
  const [reportDays, setReportDays] = useState(7);
  
  // Saving state
  const [saving, setSaving] = useState(false);

  // Fetch data on mount
  useEffect(() => {
    fetchPlacements();
    fetchCreatives();
    fetchCampaigns();
    fetchReports();
  }, []);

  // ========================
  // PLACEMENTS (Registry-based)
  // ========================
  
  const fetchPlacements = async () => {
    try {
      setLoadingPlacements(true);
      const response = await fetch(`${API}/admin/system/custom-ads/placements`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setDbPlacements(data.placements || []);
      }
    } catch (error) {
      console.error("Error fetching placements:", error);
    } finally {
      setLoadingPlacements(false);
    }
  };

  // Merge registry with DB placements
  const getMergedPlacements = () => {
    const dbMap = dbPlacements.reduce((acc, p) => {
      acc[p.placement_key] = p;
      return acc;
    }, {});
    
    // Registry placements with DB status
    const wiredPlacements = AD_PLACEMENTS.map(regPlacement => {
      const dbRecord = dbMap[regPlacement.key];
      return {
        placement_key: regPlacement.key,
        name: regPlacement.label,
        description: regPlacement.description,
        surface: regPlacement.surface,
        enabled: dbRecord?.enabled ?? false,
        inDb: !!dbRecord,
        wired: true,
      };
    });
    
    // Custom placements (in DB but not in registry)
    const customPlacements = dbPlacements
      .filter(p => !AD_PLACEMENTS.find(r => r.key === p.placement_key))
      .map(p => ({
        ...p,
        wired: false,
        inDb: true,
      }));
    
    return { wiredPlacements, customPlacements };
  };

  const togglePlacementEnabled = async (placementKey, currentEnabled) => {
    const newEnabled = !currentEnabled;
    const regPlacement = AD_PLACEMENTS.find(p => p.key === placementKey);
    
    try {
      // Check if placement exists in DB
      const existingPlacement = dbPlacements.find(p => p.placement_key === placementKey);
      
      if (existingPlacement) {
        // Update existing
        const response = await fetch(`${API}/admin/system/custom-ads/placements/${placementKey}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ enabled: newEnabled }),
        });
        
        if (response.ok) {
          toast.success(`Placement ${newEnabled ? "enabled" : "disabled"}`);
          fetchPlacements();
        } else {
          toast.error("Failed to update placement");
        }
      } else {
        // Create new placement from registry
        const response = await fetch(`${API}/admin/system/custom-ads/placements`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            placement_key: placementKey,
            name: regPlacement?.label || placementKey,
            description: regPlacement?.description || "",
            enabled: newEnabled,
          }),
        });
        
        if (response.ok) {
          toast.success(`Placement ${newEnabled ? "enabled" : "disabled"}`);
          fetchPlacements();
        } else {
          toast.error("Failed to create placement");
        }
      }
    } catch (error) {
      toast.error("Failed to update placement");
    }
  };

  const deletePlacement = async (placementKey) => {
    if (!window.confirm("Delete this custom placement?")) return;
    
    try {
      const response = await fetch(`${API}/admin/system/custom-ads/placements/${placementKey}`, {
        method: "DELETE",
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success("Placement deleted");
        fetchPlacements();
      } else {
        toast.error("Failed to delete placement");
      }
    } catch (error) {
      toast.error("Failed to delete placement");
    }
  };

  // ========================
  // CREATIVES with Image Upload
  // ========================
  
  const fetchCreatives = async () => {
    try {
      setLoadingCreatives(true);
      const response = await fetch(`${API}/admin/system/custom-ads/creatives`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setCreatives(data.creatives || []);
      }
    } catch (error) {
      console.error("Error fetching creatives:", error);
    } finally {
      setLoadingCreatives(false);
    }
  };

  const openCreativeDialog = (creative = null) => {
    if (creative) {
      setEditingCreative(creative);
      setCreativeForm({
        name: creative.name,
        creative_type: creative.creative_type || "image",
        image_url: creative.image_url || "",
        image_key: creative.image_key || "",
        headline: creative.headline || "",
        body: creative.body || "",
        cta_text: creative.cta_text || "Learn More",
        destination_url: creative.destination_url || "",
        enabled: creative.enabled,
      });
    } else {
      setEditingCreative(null);
      setCreativeForm({
        name: "",
        creative_type: "image",
        image_url: "",
        image_key: "",
        headline: "",
        body: "",
        cta_text: "Learn More",
        destination_url: "",
        enabled: true,
      });
    }
    setShowCreativeDialog(true);
  };

  // Image upload handler
  const handleImageUpload = async (file) => {
    if (!file) return;
    
    // Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload JPEG, PNG, GIF, or WebP.");
      return;
    }
    
    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("File too large. Maximum size is 5MB.");
      return;
    }
    
    setUploadingImage(true);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await fetch(`${API}/admin/system/custom-ads/upload`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        setCreativeForm(prev => ({
          ...prev,
          image_url: data.image_url,
          image_key: data.image_key || "",
        }));
        toast.success("Image uploaded successfully");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to upload image");
      }
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };

  // Drag and drop handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleImageUpload(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleImageUpload(e.target.files[0]);
    }
  };

  const saveCreative = async () => {
    if (!creativeForm.name || !creativeForm.destination_url) {
      toast.error("Name and Destination URL are required");
      return;
    }
    
    if (creativeForm.creative_type === "image" && !creativeForm.image_url) {
      toast.error("Please upload an image or provide an image URL");
      return;
    }
    
    setSaving(true);
    try {
      const url = editingCreative
        ? `${API}/admin/system/custom-ads/creatives/${editingCreative.creative_id}`
        : `${API}/admin/system/custom-ads/creatives`;
      
      const method = editingCreative ? "PUT" : "POST";
      
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(creativeForm),
      });
      
      if (response.ok) {
        toast.success(editingCreative ? "Creative updated" : "Creative created");
        setShowCreativeDialog(false);
        fetchCreatives();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save creative");
      }
    } catch (error) {
      toast.error("Failed to save creative");
    } finally {
      setSaving(false);
    }
  };

  const deleteCreative = async (creativeId) => {
    if (!window.confirm("Delete this creative?")) return;
    
    try {
      const response = await fetch(`${API}/admin/system/custom-ads/creatives/${creativeId}`, {
        method: "DELETE",
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success("Creative deleted");
        fetchCreatives();
      } else {
        toast.error("Failed to delete creative");
      }
    } catch (error) {
      toast.error("Failed to delete creative");
    }
  };

  // ========================
  // CAMPAIGNS
  // ========================
  
  const fetchCampaigns = async () => {
    try {
      setLoadingCampaigns(true);
      const response = await fetch(`${API}/admin/system/custom-ads/campaigns`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setCampaigns(data.campaigns || []);
      }
    } catch (error) {
      console.error("Error fetching campaigns:", error);
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const openCampaignDialog = (campaign = null) => {
    if (campaign) {
      setEditingCampaign(campaign);
      setCampaignForm({
        name: campaign.name,
        creative_id: campaign.creative_id,
        placement_keys: campaign.placement_keys || [],
        weight: campaign.weight || 1,
        frequency_cap_per_day: campaign.frequency_cap_per_day || 0,
        start_date: campaign.start_date?.split("T")[0] || "",
        end_date: campaign.end_date?.split("T")[0] || "",
        enabled: campaign.enabled,
      });
    } else {
      setEditingCampaign(null);
      setCampaignForm({
        name: "",
        creative_id: "",
        placement_keys: [],
        weight: 1,
        frequency_cap_per_day: 0,
        start_date: "",
        end_date: "",
        enabled: true,
      });
    }
    setShowCampaignDialog(true);
  };

  const saveCampaign = async () => {
    if (!campaignForm.name || !campaignForm.creative_id || campaignForm.placement_keys.length === 0) {
      toast.error("Name, Creative, and at least one Placement are required");
      return;
    }
    
    setSaving(true);
    try {
      const url = editingCampaign
        ? `${API}/admin/system/custom-ads/campaigns/${editingCampaign.campaign_id}`
        : `${API}/admin/system/custom-ads/campaigns`;
      
      const method = editingCampaign ? "PUT" : "POST";
      
      const payload = {
        ...campaignForm,
        start_date: campaignForm.start_date ? `${campaignForm.start_date}T00:00:00Z` : null,
        end_date: campaignForm.end_date ? `${campaignForm.end_date}T23:59:59Z` : null,
      };
      
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      
      if (response.ok) {
        toast.success(editingCampaign ? "Campaign updated" : "Campaign created");
        setShowCampaignDialog(false);
        fetchCampaigns();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save campaign");
      }
    } catch (error) {
      toast.error("Failed to save campaign");
    } finally {
      setSaving(false);
    }
  };

  const deleteCampaign = async (campaignId) => {
    if (!window.confirm("Delete this campaign?")) return;
    
    try {
      const response = await fetch(`${API}/admin/system/custom-ads/campaigns/${campaignId}`, {
        method: "DELETE",
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success("Campaign deleted");
        fetchCampaigns();
      } else {
        toast.error("Failed to delete campaign");
      }
    } catch (error) {
      toast.error("Failed to delete campaign");
    }
  };

  const togglePlacementInCampaign = (placementKey) => {
    const keys = [...campaignForm.placement_keys];
    const idx = keys.indexOf(placementKey);
    if (idx > -1) {
      keys.splice(idx, 1);
    } else {
      keys.push(placementKey);
    }
    setCampaignForm({ ...campaignForm, placement_keys: keys });
  };

  // ========================
  // REPORTS
  // ========================
  
  const fetchReports = async (days = reportDays) => {
    try {
      setLoadingReports(true);
      const response = await fetch(`${API}/admin/system/custom-ads/reports?days=${days}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setReports(data);
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
    } finally {
      setLoadingReports(false);
    }
  };

  // Get enabled placements for campaign selector
  const getEnabledPlacements = () => {
    const { wiredPlacements, customPlacements } = getMergedPlacements();
    return [...wiredPlacements, ...customPlacements].filter(p => p.enabled || p.inDb);
  };

  // ========================
  // RENDER
  // ========================
  
  const { wiredPlacements, customPlacements } = getMergedPlacements();

  return (
    <div className="space-y-6">
      <Card className="bg-zinc-800 border-zinc-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Target size={20} />
            Custom Ads Management
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Create and manage custom advertising campaigns for the platform
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeSection} onValueChange={setActiveSection}>
            <TabsList className="bg-zinc-900 border-zinc-700 mb-4">
              <TabsTrigger value="placements" className="data-[state=active]:bg-zinc-700" data-testid="custom-ads-placements-tab">
                Placements
              </TabsTrigger>
              <TabsTrigger value="creatives" className="data-[state=active]:bg-zinc-700" data-testid="custom-ads-creatives-tab">
                Creatives
              </TabsTrigger>
              <TabsTrigger value="campaigns" className="data-[state=active]:bg-zinc-700" data-testid="custom-ads-campaigns-tab">
                Campaigns
              </TabsTrigger>
              <TabsTrigger value="reports" className="data-[state=active]:bg-zinc-700" data-testid="custom-ads-reports-tab">
                Reports
              </TabsTrigger>
            </TabsList>

            {/* Placements Section - Registry Based */}
            <TabsContent value="placements">
              <div className="space-y-6">
                {/* Info Banner */}
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="text-sm text-blue-300">
                    <strong>Wired Placements</strong> are pre-defined locations in the app where ads can appear. 
                    Enable a placement to allow campaigns to target it. Custom placements are supported but require code changes to wire.
                  </p>
                </div>

                {/* Wired Placements */}
                <div>
                  <h3 className="text-lg font-medium text-white mb-3 flex items-center gap-2">
                    <PlugsConnected size={18} className="text-emerald-400" />
                    Wired Placements
                  </h3>
                  
                  {loadingPlacements ? (
                    <div className="text-zinc-400 text-center py-8">Loading placements...</div>
                  ) : (
                    <div className="grid gap-3">
                      {wiredPlacements.map((placement) => (
                        <div 
                          key={placement.placement_key} 
                          className="p-4 bg-zinc-900 rounded-lg border border-zinc-700 flex items-center justify-between"
                          data-testid={`placement-${placement.placement_key}`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-white font-medium">{placement.name}</span>
                              <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">
                                <PlugsConnected size={10} className="mr-1" />
                                Wired
                              </Badge>
                              <Badge variant="outline" className="border-zinc-600 text-zinc-400 text-xs">
                                {placement.surface}
                              </Badge>
                            </div>
                            <p className="text-sm text-zinc-500 font-mono">{placement.placement_key}</p>
                            {placement.description && (
                              <p className="text-sm text-zinc-400 mt-1">{placement.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-zinc-400">
                                {placement.enabled ? "Enabled" : "Disabled"}
                              </span>
                              <Switch
                                checked={placement.enabled}
                                onCheckedChange={() => togglePlacementEnabled(placement.placement_key, placement.enabled)}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Custom/Unwired Placements */}
                {customPlacements.length > 0 && (
                  <div>
                    <h3 className="text-lg font-medium text-white mb-3 flex items-center gap-2">
                      <Plugs size={18} className="text-amber-400" />
                      Custom Placements (Unwired)
                    </h3>
                    <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-3">
                      <p className="text-sm text-amber-300 flex items-center gap-2">
                        <Warning size={16} />
                        These placements exist in the database but are not wired to any UI surface. 
                        They won't display ads unless code is added to render them.
                      </p>
                    </div>
                    <div className="grid gap-3">
                      {customPlacements.map((placement) => (
                        <div 
                          key={placement.placement_key} 
                          className="p-4 bg-zinc-900 rounded-lg border border-amber-600/30 flex items-center justify-between"
                        >
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-white font-medium">{placement.name}</span>
                              <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                                <Plugs size={10} className="mr-1" />
                                Unwired
                              </Badge>
                            </div>
                            <p className="text-sm text-zinc-500 font-mono">{placement.placement_key}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => deletePlacement(placement.placement_key)} 
                              className="border-red-600 text-red-400 hover:bg-red-900/20"
                            >
                              <Trash size={14} />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Creatives Section with Drag/Drop Upload */}
            <TabsContent value="creatives">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-white">Ad Creatives</h3>
                  <Button onClick={() => openCreativeDialog()} className="bg-[#FF6600] hover:bg-[#CC5200]" data-testid="add-creative-btn">
                    <Plus size={16} className="mr-2" />
                    Add Creative
                  </Button>
                </div>
                
                {loadingCreatives ? (
                  <div className="text-zinc-400 text-center py-8">Loading creatives...</div>
                ) : creatives.length === 0 ? (
                  <div className="text-zinc-500 text-center py-8 border border-dashed border-zinc-700 rounded-lg">
                    No creatives yet. Create one to define your ad content.
                  </div>
                ) : (
                  <div className="grid gap-3">
                    {creatives.map((creative) => (
                      <div key={creative.creative_id} className="p-4 bg-zinc-900 rounded-lg border border-zinc-700 flex items-start justify-between" data-testid={`creative-${creative.creative_id}`}>
                        <div className="flex gap-4">
                          {creative.image_url && (
                            <img src={creative.image_url} alt="" className="w-20 h-20 object-cover rounded" />
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-white font-medium">{creative.name}</span>
                              <Badge variant="outline" className="border-zinc-600 text-zinc-400">
                                {creative.creative_type}
                              </Badge>
                              <Badge variant={creative.enabled ? "default" : "secondary"} className={creative.enabled ? "bg-emerald-500/20 text-emerald-400" : "bg-zinc-600 text-zinc-400"}>
                                {creative.enabled ? "Active" : "Disabled"}
                              </Badge>
                            </div>
                            {creative.headline && <p className="text-sm text-zinc-300 mt-1">{creative.headline}</p>}
                            <p className="text-xs text-zinc-500 mt-1 flex items-center gap-1">
                              <LinkIcon size={12} />
                              {creative.destination_url?.substring(0, 50)}{creative.destination_url?.length > 50 ? "..." : ""}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => openCreativeDialog(creative)} className="border-zinc-600 text-zinc-300 hover:bg-zinc-700">
                            <PencilSimple size={14} />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => deleteCreative(creative.creative_id)} className="border-red-600 text-red-400 hover:bg-red-900/20">
                            <Trash size={14} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Campaigns Section */}
            <TabsContent value="campaigns">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-white">Ad Campaigns</h3>
                  <Button onClick={() => openCampaignDialog()} className="bg-[#FF6600] hover:bg-[#CC5200]" data-testid="add-campaign-btn">
                    <Plus size={16} className="mr-2" />
                    Add Campaign
                  </Button>
                </div>
                
                {loadingCampaigns ? (
                  <div className="text-zinc-400 text-center py-8">Loading campaigns...</div>
                ) : campaigns.length === 0 ? (
                  <div className="text-zinc-500 text-center py-8 border border-dashed border-zinc-700 rounded-lg">
                    No campaigns yet. Create one to start serving ads.
                  </div>
                ) : (
                  <div className="grid gap-3">
                    {campaigns.map((campaign) => (
                      <div key={campaign.campaign_id} className="p-4 bg-zinc-900 rounded-lg border border-zinc-700" data-testid={`campaign-${campaign.campaign_id}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-white font-medium">{campaign.name}</span>
                              <Badge variant={campaign.enabled ? "default" : "secondary"} className={campaign.enabled ? "bg-emerald-500/20 text-emerald-400" : "bg-zinc-600 text-zinc-400"}>
                                {campaign.enabled ? "Active" : "Disabled"}
                              </Badge>
                            </div>
                            <p className="text-sm text-zinc-400 mt-1">
                              Creative: <span className="text-zinc-300">{campaign.creative_name}</span>
                            </p>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {campaign.placement_keys?.map((pk) => (
                                <Badge key={pk} variant="outline" className={`border-zinc-600 text-zinc-400 text-xs ${isPlacementWired(pk) ? "" : "border-amber-600 text-amber-400"}`}>
                                  {isPlacementWired(pk) ? "" : "⚠ "}{pk}
                                </Badge>
                              ))}
                            </div>
                            <div className="flex gap-4 mt-2 text-xs text-zinc-500">
                              <span>Weight: {campaign.weight}</span>
                              <span>Freq Cap: {campaign.frequency_cap_per_day || "None"}/day</span>
                              {campaign.start_date && <span>Start: {campaign.start_date.split("T")[0]}</span>}
                              {campaign.end_date && <span>End: {campaign.end_date.split("T")[0]}</span>}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => openCampaignDialog(campaign)} className="border-zinc-600 text-zinc-300 hover:bg-zinc-700">
                              <PencilSimple size={14} />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => deleteCampaign(campaign.campaign_id)} className="border-red-600 text-red-400 hover:bg-red-900/20">
                              <Trash size={14} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Reports Section */}
            <TabsContent value="reports">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-white">Performance Reports</h3>
                  <div className="flex items-center gap-2">
                    <Select value={String(reportDays)} onValueChange={(v) => { setReportDays(Number(v)); fetchReports(Number(v)); }}>
                      <SelectTrigger className="w-32 bg-zinc-900 border-zinc-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="7">Last 7 days</SelectItem>
                        <SelectItem value="14">Last 14 days</SelectItem>
                        <SelectItem value="30">Last 30 days</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={() => fetchReports()} className="border-zinc-600 text-zinc-300 hover:bg-zinc-700">
                      <ArrowClockwise size={16} />
                    </Button>
                  </div>
                </div>
                
                {loadingReports ? (
                  <div className="text-zinc-400 text-center py-8">Loading reports...</div>
                ) : !reports ? (
                  <div className="text-zinc-500 text-center py-8">No data available</div>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 bg-zinc-900 rounded-lg border border-zinc-700 text-center">
                        <p className="text-2xl font-bold text-white">{reports.totals?.impressions || 0}</p>
                        <p className="text-sm text-zinc-400">Impressions</p>
                      </div>
                      <div className="p-4 bg-zinc-900 rounded-lg border border-zinc-700 text-center">
                        <p className="text-2xl font-bold text-white">{reports.totals?.clicks || 0}</p>
                        <p className="text-sm text-zinc-400">Clicks</p>
                      </div>
                      <div className="p-4 bg-zinc-900 rounded-lg border border-zinc-700 text-center">
                        <p className="text-2xl font-bold text-[#FF6600]">{reports.totals?.ctr || 0}%</p>
                        <p className="text-sm text-zinc-400">CTR</p>
                      </div>
                    </div>
                    
                    {reports.by_campaign?.length > 0 && (
                      <div className="mt-6">
                        <h4 className="text-sm font-medium text-zinc-300 mb-3">By Campaign</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-zinc-700 text-left">
                                <th className="pb-2 text-zinc-400 font-medium">Campaign</th>
                                <th className="pb-2 text-zinc-400 font-medium text-right">Impressions</th>
                                <th className="pb-2 text-zinc-400 font-medium text-right">Clicks</th>
                                <th className="pb-2 text-zinc-400 font-medium text-right">CTR</th>
                              </tr>
                            </thead>
                            <tbody>
                              {reports.by_campaign.map((row) => (
                                <tr key={row.campaign_id} className="border-b border-zinc-800">
                                  <td className="py-2 text-white">{row.campaign_name}</td>
                                  <td className="py-2 text-zinc-300 text-right">{row.impressions}</td>
                                  <td className="py-2 text-zinc-300 text-right">{row.clicks}</td>
                                  <td className="py-2 text-[#FF6600] text-right">{row.ctr}%</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Creative Dialog with Drag/Drop Upload */}
      <AdminRecordModal
        open={showCreativeDialog}
        onOpenChange={setShowCreativeDialog}
        title={editingCreative ? "Edit Creative" : "Create Creative"}
        description="Creatives define the content shown in your ads."
        size="md"
        footer={
          <AdminModalFooter onCancel={() => setShowCreativeDialog(false)}>
            <Button onClick={saveCreative} disabled={saving} className="bg-[#FF6600] hover:bg-[#CC5200]" data-testid="save-creative-btn">
              {saving ? "Saving..." : "Save"}
            </Button>
          </AdminModalFooter>
        }
      >
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-zinc-300">Name</Label>
          <Input
            value={creativeForm.name}
            onChange={(e) => setCreativeForm({ ...creativeForm, name: e.target.value })}
            placeholder="Holiday Promo Banner"
            className="bg-zinc-900 border-zinc-600 text-white"
            data-testid="creative-name-input"
          />
        </div>
        
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-zinc-300">Type</Label>
          <Select value={creativeForm.creative_type} onValueChange={(v) => setCreativeForm({ ...creativeForm, creative_type: v })}>
            <SelectTrigger className="bg-zinc-900 border-zinc-600 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="image">Image Banner</SelectItem>
              <SelectItem value="native">Native Card</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Image Upload Area */}
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-zinc-300">Creative Image</Label>
          
          {/* Preview */}
          {creativeForm.image_url && (
            <div className="relative mb-2 inline-block">
              <img 
                src={creativeForm.image_url} 
                alt="Preview" 
                className="max-h-32 rounded border border-zinc-700"
              />
              <button
                type="button"
                onClick={() => setCreativeForm({ ...creativeForm, image_url: "", image_key: "" })}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
              >
                <X size={12} />
              </button>
            </div>
          )}
          
          {/* Drag/Drop Zone */}
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`
              border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
              ${dragActive 
                ? "border-[#FF6600] bg-[#FF6600]/10" 
                : "border-zinc-600 hover:border-zinc-500 bg-zinc-900/50"
              }
              ${uploadingImage ? "opacity-50 pointer-events-none" : ""}
            `}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Upload size={32} className="mx-auto mb-2 text-zinc-400" />
            {uploadingImage ? (
              <p className="text-zinc-400">Uploading...</p>
            ) : (
              <>
                <p className="text-zinc-300">Drag & drop an image here</p>
                <p className="text-zinc-500 text-sm mt-1">or click to browse</p>
                <p className="text-zinc-600 text-xs mt-2">JPEG, PNG, GIF, WebP • Max 5MB</p>
              </>
            )}
          </div>
          
          {/* Fallback URL Input */}
          <details className="mt-2">
            <summary className="text-xs text-zinc-500 cursor-pointer hover:text-zinc-400">
              Or enter image URL manually
            </summary>
            <Input
              value={creativeForm.image_url}
              onChange={(e) => setCreativeForm({ ...creativeForm, image_url: e.target.value, image_key: "" })}
              placeholder="https://example.com/banner.jpg"
              className="mt-2 bg-zinc-900 border-zinc-600 text-white text-sm"
            />
          </details>
        </div>
        
        {creativeForm.creative_type === "native" && (
          <>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-zinc-300">Headline</Label>
              <Input
                value={creativeForm.headline}
                onChange={(e) => setCreativeForm({ ...creativeForm, headline: e.target.value })}
                placeholder="Upgrade to Pro Today!"
                className="bg-zinc-900 border-zinc-600 text-white"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-zinc-300">Body Text</Label>
              <Textarea
                value={creativeForm.body}
                onChange={(e) => setCreativeForm({ ...creativeForm, body: e.target.value })}
                placeholder="Get access to premium features..."
                className="bg-zinc-900 border-zinc-600 text-white"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-zinc-300">CTA Button Text</Label>
              <Input
                value={creativeForm.cta_text}
                onChange={(e) => setCreativeForm({ ...creativeForm, cta_text: e.target.value })}
                placeholder="Learn More"
                className="bg-zinc-900 border-zinc-600 text-white"
              />
            </div>
          </>
        )}
        
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-zinc-300">Destination URL</Label>
          <Input
            value={creativeForm.destination_url}
            onChange={(e) => setCreativeForm({ ...creativeForm, destination_url: e.target.value })}
            placeholder="https://example.com/landing-page"
            className="bg-zinc-900 border-zinc-600 text-white"
            data-testid="creative-destination-input"
          />
          <p className="text-xs text-zinc-500">Where users go when they click</p>
        </div>
        
        <div className="flex items-center justify-between py-2">
          <Label className="text-sm font-medium text-zinc-300">Enabled</Label>
          <Switch
            checked={creativeForm.enabled}
            onCheckedChange={(checked) => setCreativeForm({ ...creativeForm, enabled: checked })}
          />
        </div>
      </AdminRecordModal>

      {/* Campaign Dialog */}
      <AdminRecordModal
        open={showCampaignDialog}
        onOpenChange={setShowCampaignDialog}
        title={editingCampaign ? "Edit Campaign" : "Create Campaign"}
        description="Campaigns link creatives to placements with scheduling."
        size="md"
        footer={
          <AdminModalFooter onCancel={() => setShowCampaignDialog(false)}>
            <Button onClick={saveCampaign} disabled={saving} className="bg-[#FF6600] hover:bg-[#CC5200]" data-testid="save-campaign-btn">
              {saving ? "Saving..." : "Save"}
            </Button>
          </AdminModalFooter>
        }
      >
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-zinc-300">Campaign Name</Label>
          <Input
            value={campaignForm.name}
            onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
            placeholder="Q1 Promo Campaign"
            className="bg-zinc-900 border-zinc-600 text-white"
            data-testid="campaign-name-input"
          />
        </div>
        
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-zinc-300">Creative</Label>
          <Select value={campaignForm.creative_id} onValueChange={(v) => setCampaignForm({ ...campaignForm, creative_id: v })}>
            <SelectTrigger className="bg-zinc-900 border-zinc-600 text-white" data-testid="campaign-creative-select">
              <SelectValue placeholder="Select a creative" />
            </SelectTrigger>
            <SelectContent>
              {creatives.filter(c => c.enabled).map((c) => (
                <SelectItem key={c.creative_id} value={c.creative_id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-zinc-300">Placements</Label>
          <div className="mt-2 space-y-2 p-3 rounded-lg border border-zinc-700/50 bg-zinc-800/30 max-h-48 overflow-y-auto">
            {getEnabledPlacements().length === 0 ? (
              <p className="text-zinc-500 text-sm">No placements enabled. Enable placements in the Placements tab first.</p>
            ) : (
              getEnabledPlacements().map((p) => (
                <div key={p.placement_key} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id={`campaign-placement-${p.placement_key}`}
                    checked={campaignForm.placement_keys.includes(p.placement_key)}
                    onChange={() => togglePlacementInCampaign(p.placement_key)}
                    className="rounded border-zinc-600 bg-zinc-900"
                  />
                  <label htmlFor={`campaign-placement-${p.placement_key}`} className="text-sm text-zinc-300 flex items-center gap-1">
                    {p.name}
                    {p.wired ? (
                      <span className="text-emerald-400 text-xs">(wired)</span>
                    ) : (
                      <span className="text-amber-400 text-xs">(unwired)</span>
                    )}
                  </label>
                </div>
              ))
            )}
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-zinc-300">Weight</Label>
            <Input
              type="number"
              min="1"
              value={campaignForm.weight}
              onChange={(e) => setCampaignForm({ ...campaignForm, weight: parseInt(e.target.value) || 1 })}
              className="bg-zinc-900 border-zinc-600 text-white"
            />
            <p className="text-xs text-zinc-500">Higher = more likely</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-zinc-300">Frequency Cap/Day</Label>
            <Input
              type="number"
              min="0"
              value={campaignForm.frequency_cap_per_day}
              onChange={(e) => setCampaignForm({ ...campaignForm, frequency_cap_per_day: parseInt(e.target.value) || 0 })}
              className="bg-zinc-900 border-zinc-600 text-white"
            />
            <p className="text-xs text-zinc-500">0 = no cap</p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-zinc-300">Start Date</Label>
            <Input
              type="date"
              value={campaignForm.start_date}
              onChange={(e) => setCampaignForm({ ...campaignForm, start_date: e.target.value })}
              className="bg-zinc-900 border-zinc-600 text-white"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-zinc-300">End Date</Label>
            <Input
              type="date"
              value={campaignForm.end_date}
              onChange={(e) => setCampaignForm({ ...campaignForm, end_date: e.target.value })}
              className="bg-zinc-900 border-zinc-600 text-white"
            />
          </div>
        </div>
        
        <div className="flex items-center justify-between py-2">
          <Label className="text-sm font-medium text-zinc-300">Enabled</Label>
          <Switch
            checked={campaignForm.enabled}
            onCheckedChange={(checked) => setCampaignForm({ ...campaignForm, enabled: checked })}
          />
        </div>
      </AdminRecordModal>
    </div>
  );
}

export default CustomAdsPanel;
